import { Injectable } from '@angular/core';
import { ICommentModel } from '@fi-app/shared/models';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AppTextService } from '@fi-shared/app-text';
import { IFiDialogResult } from '@fi-app/shared/fi-dialogs/fi-dialogs';
import { isNullOrUndefined } from '@fi-app/shared/utils/is-null-or-undefined';
import { FiCommentsListStateService } from './fi-comments-list-state.service';
import { ObjectTypes } from '@fi-app/shared/types';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';

@Injectable()
export class FiCommentsListHelperService {
    public readonly loadMoreCommentsButtonIdPrefix: string = 'load-more-comments-button-';
    public readonly commentIdPrefix: string = 'fi-comment-';

    constructor(
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private commentsListStateService: FiCommentsListStateService,
        private userDataService: FiUserDataService,
    ) {}

    public showDeleteConfirmationDialog(hasReplies: boolean): IFiDialogResult {
        return this.fiDialogsService.confirmSimpleDelete({
            headerText: this.appTextService.get(
                hasReplies ? 'comment.delete_comment_with_replies' : 'comment.delete_comment',
            ),
            messageText: this.appTextService.get(
                hasReplies
                    ? 'comment.delete_comment_with_replies_confirmation_message'
                    : 'comment.delete_comment_confirmation_message',
            ),
            preventUppercaseButtonLabel: true,
        });
    }

    public isCommentChanged(comment: ICommentModel): boolean {
        if (!comment.CreatedOn || !comment.ChangedOn) {
            return false;
        }
        return new Date(comment.CreatedOn).getTime() !== new Date(comment.ChangedOn).getTime();
    }

    public getCommentId(commentId: number): string {
        return this.commentIdPrefix + commentId;
    }

    public getLoadMoreCommentsButtonId(commentId: number): string {
        return this.loadMoreCommentsButtonIdPrefix + commentId;
    }

    public getTotalChildrenCount(comment: ICommentModel): number {
        return this.commentsListStateService.flattenCommentsChildren(comment).length;
    }

    public getNewCommentInstance(objectId: number, objectType: ObjectTypes, parentCommentId: number): ICommentModel {
        const newComment: ICommentModel = {
            ObjectId: objectId,
            ObjectType: objectType,
            Content: undefined,
            ChildItems: [],
            ParentId: parentCommentId,
            UserProfile: this.userDataService.getUserProfile(),
            MediaBankFileReferences: [],
            Links: [],
            Reactions: [],
            TaggedUsers: [],
        };
        return newComment;
    }

    public getCommentReplyButtonText(isMainComment: boolean, comment: ICommentModel): string {
        const totalChildrenCount: number = isMainComment ? this.getTotalChildrenCount(comment) : 0;
        const buttonLabel: string = this.appTextService.get('common.reply');
        return buttonLabel + (!!totalChildrenCount ? ` ( ${totalChildrenCount})` : '');
    }

    public isCommentsConnectionShown(isMainComment: boolean, comment: ICommentModel): boolean {
        return isMainComment && !!comment?.ChildItems?.length && this.getTotalChildrenCount(comment) > 0;
    }

    public getCommentsConnectionLineOffset(
        connectionLineTargetElement: HTMLElement,
        comment: ICommentModel,
        isMainComment: boolean,
        isDateShownAtTop: boolean,
    ): number {
        if (isNullOrUndefined(connectionLineTargetElement)) {
            return 0;
        }
        const isLoadMoreCommentsButtonShown = isMainComment && this.getTotalChildrenCount(comment) > 1;
        const parentCommentRect: DOMRect = document
            .getElementById(this.getCommentId(comment.Id))
            .getBoundingClientRect();
        const targetRect: DOMRect = connectionLineTargetElement.getBoundingClientRect();

        if (isLoadMoreCommentsButtonShown) {
            const extraOffset: number = isDateShownAtTop ? 30 : 10; // button styles related offset
            const distanceToTarget: number = targetRect.top - parentCommentRect.top - extraOffset;
            return distanceToTarget - targetRect.height / 2;
        } else {
            const connectionLineStartOffset: number = 47; // main comment profile height + small offset
            const distanceToChildProfile: number = 45; // 21px (child middle profile) + 24px (child margin-top)
            return parentCommentRect.height - connectionLineStartOffset + distanceToChildProfile;
        }
    }

    public isShowMoreCommentsButtonShown(isMainComment: boolean, comment: ICommentModel): boolean {
        return this.isCommentsConnectionShown(isMainComment, comment) && this.getTotalChildrenCount(comment) > 1;
    }

    public getConnectionTargetElement(isMainComment: boolean, comment: ICommentModel): HTMLElement {
        if (!comment?.ChildItems?.length || !this.isCommentsConnectionShown(isMainComment, comment)) {
            return null;
        }

        const isLoadMoreCommentsButtonShown = this.isShowMoreCommentsButtonShown(isMainComment, comment);
        const targetId = isLoadMoreCommentsButtonShown
            ? this.getLoadMoreCommentsButtonId(comment.Id)
            : comment.ChildItems[0]
              ? this.getCommentId(comment.ChildItems[0].Id)
              : null;

        return targetId ? document.getElementById(targetId) : null;
    }

    public isEditActionEnabled(CreatedByUserId: number): boolean {
        return CreatedByUserId === this.userDataService.getUserId();
    }

    public isRemoveActionEnabled(CreatedByUserId: number, hasDeleteAccessOfOwnerItem: boolean): boolean {
        return CreatedByUserId === this.userDataService.getUserId() || hasDeleteAccessOfOwnerItem;
    }
}
